// src/hooks/useExperimentosAtivos.js
import { useEffect, useRef, useState } from 'react';
import { subscribeTenantStream } from '../infra/WebSocketService';

/**
 * Quais dispositivos ACABARAM de transmitir com experimento rodando, no formato
 * `{ [numeroSerie]: experimentId }`.
 *
 * Existe separado do `useTenantTelemetry` por duas razões:
 *
 * 1. CUSTO DE RENDER. Aquele hook faz `setState` a cada frame — ótimo para a
 *    página de um dispositivo (que desenha gráficos ao vivo), péssimo para uma
 *    página que renderiza um grid de dezenas de cards: seriam várias
 *    re-renderizações por segundo no celular. Aqui o estado só muda quando um
 *    aparelho INICIA ou PARA um experimento. Um aparelho rodando manda o mesmo
 *    id em todo frame, então em regime permanente o custo é zero re-render.
 *    (É também por isso que este hook não expõe um `lastUpdated`: manter o
 *    horário do último frame significaria um `setState` por frame, que é
 *    exatamente o que ele existe para evitar.)
 *
 * 2. O SNAPSHOT NÃO SERVE DE PROVA. O iot-hub guarda o último estado conhecido
 *    de cada aparelho e o reenvia inteiro a quem conecta — sem expirar nunca
 *    (`telemetry/store.go`, BuildSnapshot). Um aparelho que encerrou sem mandar
 *    um frame ocioso continua no snapshot com o `experimentId` antigo, por
 *    horas. Contar isso como "rodando" faria uma análise NOVA aparecer com
 *    dispositivos já vinculados antes de qualquer tubo ser encaixado. Por isso
 *    aqui só conta o que chega ao vivo (delta) depois que assinamos: um
 *    aparelho de fato rodando publica em segundos, então nada se perde.
 *
 * A lógica de WebSocket (reconexão com backoff, snapshot no connect, parsing de
 * snapshot x delta) NÃO é duplicada: continua em
 * `infra/WebSocketService.subscribeTenantStream`.
 *
 * @param {Object}  opts
 * @param {boolean} opts.enabled  Assina só quando true. Ao virar false o socket
 *                                é fechado e o mapa zerado — a página não deve
 *                                manter conexão aberta, nem carregar leitura
 *                                velha, quando não há nada acontecendo.
 */
export default function useExperimentosAtivos({ enabled = true } = {}) {
    const [experimentoPorSerial, setExperimentoPorSerial] = useState({});
    const [connected, setConnected] = useState(false);
    const ctrlRef = useRef(null);

    // Só assina com a aba visível: sem isso o socket segue recebendo frames com
    // a tela no bolso do laboratorista. Nada se perde ao voltar — o aparelho
    // que estiver rodando manda outro frame em segundos.
    const [visivel, setVisivel] = useState(
        () =>
            typeof document === 'undefined' ||
            document.visibilityState !== 'hidden'
    );

    useEffect(() => {
        const onVisibility = () =>
            setVisivel(document.visibilityState !== 'hidden');
        document.addEventListener('visibilitychange', onVisibility);
        return () =>
            document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    const ativo = enabled && visivel;

    useEffect(() => {
        if (!ativo) {
            try {
                ctrlRef.current?.close();
            } catch {}
            ctrlRef.current = null;
            setConnected(false);
            // Zera: uma leitura de minutos atrás não pode ressurgir como
            // verdade quando a página voltar a ficar ao vivo.
            setExperimentoPorSerial((anterior) =>
                Object.keys(anterior).length ? {} : anterior
            );
            return undefined;
        }

        let myCtrl = null;
        try {
            // Mesma guarda documentada em useTenantTelemetry: o close() é
            // assíncrono, então um callback tardio do socket antigo pode rodar
            // depois do onOpen do novo e zerar o `connected` de quem já estava
            // conectado. Comparar a identidade do controller resolve.
            myCtrl = subscribeTenantStream(
                (norm) => {
                    if (ctrlRef.current !== myCtrl) return;

                    // Snapshot é estado acumulado (possivelmente velho) — ver o
                    // motivo 2 no topo. Só o delta prova "está rodando agora".
                    if (Array.isArray(norm?._raw?.devices)) return;

                    const serial = String(
                        norm?._raw?.serialNumber || ''
                    ).trim();
                    if (!serial) return;

                    const id =
                        norm?.devicesBySerial?.[serial]?.experimentId || '';

                    setExperimentoPorSerial((anterior) => {
                        if ((anterior[serial] || '') === id) return anterior;
                        const proximo = { ...anterior };
                        if (id) proximo[serial] = id;
                        else delete proximo[serial];
                        return proximo;
                    });
                },
                {
                    onOpen: () => {
                        if (ctrlRef.current !== myCtrl) return;
                        setConnected(true);
                    },
                    onClose: () => {
                        if (ctrlRef.current !== myCtrl) return;
                        setConnected(false);
                    },
                    onError: () => {
                        if (ctrlRef.current !== myCtrl) return;
                        setConnected(false);
                    },
                }
            );
            ctrlRef.current = myCtrl;
        } catch {
            // subscribeTenantStream lança se não identificar o tenant na URL.
            // As rotas são /:tenant/*, então na prática não acontece — mas um
            // throw aqui derrubaria a página inteira, o que é pior que ficar
            // sem tempo real.
            setConnected(false);
        }

        return () => {
            try {
                myCtrl?.close();
            } catch {}
            if (ctrlRef.current === myCtrl) ctrlRef.current = null;
        };
    }, [ativo]);

    return { experimentoPorSerial, connected };
}
